import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { t, productName } from "../lib/i18n";
import { Wallet, CheckCircle2, AlertCircle, X, CreditCard, Gift } from "lucide-react";
import { toast } from "sonner";

// Stripe is staged but disabled until the live key is wired up via env.
// REACT_APP_STRIPE_ENABLED="true" + a backend STRIPE_SECRET_KEY will reveal it.
const STRIPE_ENABLED =
  (process.env.REACT_APP_STRIPE_ENABLED || "").toLowerCase() === "true";

export default function CustomerAccount({ lang, onReload }) {
  const [summary, setSummary] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/my-account-summary");
      setSummary(r.data);
    } catch {
      setSummary(null);
    }
  };
  useEffect(() => {
    load();
    // Poll så custom payments oppdateres til «Betalt» automatisk etter Stripe-retur.
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const cancelOrder = async (id) => {
    if (!window.confirm(t(lang, "extras_cancel") + "?")) return;
    try {
      await api.patch(`/orders/${id}/cancel`);
      toast.success(t(lang, "extras_order_cancelled"));
      load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const payViaStripe = (order) => {
    const url = order.stripe_payment_url
      || (order.items || []).map((i) => i.stripe_payment_url).find(Boolean);
    if (!url) {
      toast.error(lang === "no" ? "Ingen Stripe-lenke på denne ordren" : "No Stripe link on this order");
      return;
    }
    localStorage.setItem("cbt:pending_stripe_order", order.id);
    const sep = url.includes("?") ? "&" : "?";
    window.location.href = `${url}${sep}client_reference_id=${encodeURIComponent(order.id)}`;
  };

  if (!summary || !summary.booking_id) return null;

  const outstandingOrders = (summary.orders || []).filter(
    (o) => o.status === "pending" && !o.included_with_booking
  );
  const paidOrders = (summary.orders || []).filter(
    (o) => o.status === "paid" && !o.included_with_booking
  );
  const includedOrders = (summary.orders || []).filter(
    (o) => o.included_with_booking && o.status !== "cancelled"
  );
  const hasAnyOutstanding = summary.outstanding_total > 0;

  return (
    <Card className="p-5" data-testid="customer-account">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-5 h-5 text-[#1DA3B3]" />
        <h2 className="text-xl font-bold text-[#0C1824]">{t(lang, "account_title")}</h2>
      </div>

      {/* Included with booking – prominently shown so customer immediately
          sees what's already covered when they first log in. */}
      {includedOrders.length > 0 && (
        <div
          className="mb-4 p-4 rounded-md border-l-4 border-l-emerald-500 bg-emerald-50"
          data-testid="customer-included-section"
        >
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-emerald-700" />
            <div className="text-xs uppercase tracking-[0.18em] text-emerald-900 font-semibold">
              {t(lang, "account_included_title")}
            </div>
          </div>
          <p className="text-xs text-emerald-900/80 mb-2">
            {t(lang, "account_included_subtitle")}
          </p>
          <ul className="space-y-1 text-sm text-[#0C1824]">
            {includedOrders.flatMap((o) =>
              (o.items || []).map((it, i) => (
                <li
                  key={`${o.id}-${i}`}
                  data-testid={`customer-included-item-${o.id}-${i}`}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 rounded bg-white/60"
                >
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                    <span>
                      <span className="font-mono text-emerald-700 mr-1">×{it.quantity}</span>
                      {productName(it, lang)}
                    </span>
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          data-testid="account-arrival-due"
          className={`p-4 rounded-md border-l-4 ${
            summary.arrival_due_paid
              ? "border-l-emerald-500 bg-emerald-50"
              : summary.arrival_due_amount > 0
              ? "border-l-[#BD984D] bg-[#BD984D]/10"
              : "border-l-slate-300 bg-slate-50"
          }`}
        >
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
            {t(lang, "account_arrival_due")}
          </div>
          <div className="text-2xl font-bold text-[#0C1824] mt-1">
            {summary.arrival_due_amount || 0} €
          </div>
          <div className="text-xs text-[#2B3F4D] mt-1 flex items-center gap-1">
            {summary.arrival_due_amount > 0 ? (
              summary.arrival_due_paid ? (
                <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> {t(lang, "account_arrival_paid")}</>
              ) : (
                <><AlertCircle className="w-3.5 h-3.5 text-[#BD984D]" /> {t(lang, "account_arrival_unpaid")}</>
              )
            ) : "—"}
          </div>
          {summary.arrival_due_note && (
            <div className="text-xs italic text-[#2B3F4D] mt-1.5">«{summary.arrival_due_note}»</div>
          )}
        </div>

        <div
          data-testid="account-orders-outstanding"
          className="p-4 rounded-md border-l-4 border-l-[#1DA3B3] bg-[#1DA3B3]/5"
        >
          <div className="text-xs uppercase tracking-[0.18em] text-[#A57F39]">
            {t(lang, "account_orders_outstanding")}
          </div>
          <div className="text-2xl font-bold text-[#0C1824] mt-1">
            {summary.orders_outstanding_total || 0} €
          </div>
          <div className="text-xs text-[#2B3F4D] mt-1">
            {outstandingOrders.length} {outstandingOrders.length === 1 ? "ordre" : "ordrer"}
          </div>
        </div>
      </div>

      {hasAnyOutstanding && (
        <div className="mt-4 p-4 bg-[#0C1824] text-white rounded-md" data-testid="account-total-outstanding">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#ECC974]">
                {t(lang, "account_total_outstanding")}
              </div>
              <div className="text-3xl font-bold">{summary.outstanding_total} €</div>
            </div>
            {STRIPE_ENABLED ? (
              <Button
                data-testid="account-pay-now"
                onClick={() => toast.info("Stripe Checkout starter…")}
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {t(lang, "account_pay_button")}
              </Button>
            ) : (
              <div className="text-xs text-slate-300 max-w-xs text-right">
                {t(lang, "account_pay_at_checkin")}
              </div>
            )}
          </div>
        </div>
      )}

      {outstandingOrders.length > 0 && (
        <div className="mt-4 space-y-2">
          {outstandingOrders.map((o) => (
            <div
              key={o.id}
              data-testid={`order-row-${o.id}`}
              className="p-3 border border-slate-200 rounded-md"
            >
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex-1 min-w-0">
                  <Badge className="bg-[#BD984D]/15 text-[#A57F39] text-[10px] mb-1.5">
                    {t(lang, "order_status_pending")} · {new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "nb-NO")}
                  </Badge>
                  <ul className="text-sm space-y-0.5">
                    {(o.items || []).map((it, i) => (
                      <li key={i} className="flex items-center gap-2 text-[#2B3F4D]">
                        <span>× {it.quantity}</span>
                        <span>{lang === "en" ? it.name_en : it.name_no}</span>
                        <span className="ml-auto font-mono text-[#A57F39]">{it.line_total_eur} €</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex flex-col gap-1.5 items-end">
                  {(o.stripe_payment_url || (o.items || []).some((i) => i.stripe_payment_url)) && (
                    <Button
                      size="sm"
                      onClick={() => payViaStripe(o)}
                      data-testid={`order-pay-stripe-${o.id}`}
                      className="bg-[#635BFF] hover:bg-[#4F46E5] text-white h-8"
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1" />
                      {lang === "no" ? "Betal med kort" : "Pay by card"}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => cancelOrder(o.id)}
                    data-testid={`order-cancel-${o.id}`}
                    className="border-red-200 text-red-600 hover:bg-red-50 h-8"
                  >
                    <X className="w-3.5 h-3.5 mr-1" />
                    {t(lang, "extras_cancel")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {paidOrders.length > 0 && (
        <details className="mt-4">
          <summary className="text-xs uppercase tracking-[0.18em] text-[#A57F39] cursor-pointer">
            {t(lang, "account_orders_paid")} ({paidOrders.length})
          </summary>
          <div className="mt-2 space-y-1.5 text-xs">
            {paidOrders.map((o) => (
              <div key={o.id} className="text-[#2B3F4D] flex justify-between" data-testid={`paid-order-${o.id}`}>
                <span>{new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-GB" : "nb-NO")} · {o.items.length} items</span>
                <span className="font-mono">{o.subtotal_eur} € <CheckCircle2 className="inline w-3 h-3 text-emerald-600 ml-1" /></span>
              </div>
            ))}
          </div>
        </details>
      )}
    </Card>
  );
}
